import { useCallback, useMemo, useState } from 'react'
import { NetworkStatus, useQuery } from '@apollo/client'

import { GET_FAVORITES_TERRITORIES_QUERY } from './query'

export interface FavoriteTerritory {
  name: string
  territoryId: string
}

export function useFavoriteTerritories() {
  const [selectedFavorite, setSelectedFavorite] = useState<FavoriteTerritory | null>(null)

  const {
    networkStatus: favoritesNetworkStatus,
    error: favoritesError,
    data: favoritesData,
    refetch: refetchFavorites,
  } = useQuery(GET_FAVORITES_TERRITORIES_QUERY, {
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ getWorkspace }) => {
      setSelectedFavorite(getWorkspace?.followedTerritories?.data?.[0])
    },
  })

  const favorites = useMemo(() => {
    const responseData = Array.from(favoritesData?.getWorkspace?.followedTerritories?.data || [])
    const parsedFavorites: FavoriteTerritory[] = responseData.map((item: any) => ({
      name: item.name,
      territoryId: item.territoryId,
    }))

    return parsedFavorites
  }, [favoritesData?.getWorkspace?.followedTerritories?.data])

  const isFavoritesLoading = [NetworkStatus.loading, NetworkStatus.refetch, NetworkStatus.setVariables].includes(
    favoritesNetworkStatus
  )

  const selectFavorite = useCallback((favorite: FavoriteTerritory) => {
    setSelectedFavorite(favorite)
  }, [])

  const memoizedValues = useMemo(
    () => ({
      selectedFavorite,
      favorites,
      isFavoritesLoading,
      isFavoritesError: !!favoritesError,
      refetchFavorites,
      selectFavorite,
    }),
    [selectedFavorite, favorites, favoritesError, isFavoritesLoading, refetchFavorites, selectFavorite]
  )

  return memoizedValues
}
